import React, { useState } from 'react';
import { Button } from 'react-bootstrap';
import { Tooltip } from 'react-tooltip';
import { PublishTemplateModal } from './PublishTemplateModal';
import { TemplateType } from '../../consts/forms-types';
import './PublishChangesButton.scss';

interface PublishChangesButtonProps {
  templateId: string;
  templateName: string;
  templateType?: TemplateType;
  hasPendingChanges: boolean;
  hasPendingSuggestions: boolean;
  canPublish: boolean;
  canManageTemplates: boolean;
  onPublishSuccess?: () => void;
  isDocxNative?: boolean;
}

export const PublishChangesButton: React.FC<PublishChangesButtonProps> = ({
  templateId,
  templateName,
  templateType,
  hasPendingChanges,
  hasPendingSuggestions,
  canPublish,
  canManageTemplates,
  onPublishSuccess,
  isDocxNative = false,
}) => {
  const [isModalOpen, setIsModalOpen] = useState(false);

  if (!canManageTemplates) {
    return null;
  }

  const handleOpenModal = () => {
    setIsModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
  };

  const handlePublishSuccess = () => {
    onPublishSuccess?.();
  };

  const getTooltipMessage = () => {
    if (hasPendingSuggestions) {
      return 'Resolve all pending suggestions before publishing';
    }
    if (!hasPendingChanges) {
      return 'No changes to publish';
    }
    return 'Publish draft changes to make them live for document creation';
  };

  const button = (
    <Button
      variant="success"
      className={`publish-changes-btn ${hasPendingSuggestions ? 'has-pending-suggestions' : ''}`}
      onClick={handleOpenModal}
      disabled={!canPublish}
    >
      Publish Changes
    </Button>
  );

  const renderButton = () => {
    if (!canPublish) {
      const tooltipId = `publish-button-tooltip-${templateId}`;
      return (
        <>
          <span id={tooltipId} className="d-inline-block" style={{ cursor: 'not-allowed' }}>
            {button}
          </span>
          <Tooltip
            anchorSelect={`#${tooltipId}`}
            place="bottom"
            className="publish-button-tooltip"
            style={{
              maxWidth: '220px',
              zIndex: 1060,
              fontSize: '13px',
              padding: '8px 12px',
              borderRadius: '4px',
              textAlign: 'center',
            }}
            opacity={1}
          >
            {getTooltipMessage()}
          </Tooltip>
        </>
      );
    }
    return button;
  };

  return (
    <>
      {renderButton()}

      <PublishTemplateModal
        isOpen={isModalOpen}
        onClose={handleCloseModal}
        templateId={templateId}
        templateName={templateName}
        templateType={templateType}
        hasPendingSuggestions={hasPendingSuggestions}
        onPublishSuccess={handlePublishSuccess}
        isDocxNative={isDocxNative}
      />
    </>
  );
};
